import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Loader2, Check } from "lucide-react";
import { useClientAuth } from "../context/ClientAuth";
import { EDITORIAL_IMAGES } from "../lib/editorialImages";
import { Checkbox } from "../components/ui/checkbox";

const HERO_IMG = EDITORIAL_IMAGES.join;

const Field = ({ icon: Icon, ...props }) => (
  <div className="flex items-center gap-3 border border-bone/25 bg-bone/5 rounded-md px-4 py-3 focus-within:border-bone/60 transition-colors">
    <Icon size={16} className="text-mocha shrink-0" strokeWidth={1.5} />
    <input {...props} className="w-full bg-transparent text-sm text-bone placeholder:text-bone/40 outline-none" />
  </div>
);

const PERKS = [
  "First dibs on tonight's last-minute openings",
  "Members-only prices across Melbourne studios",
  "Book beauty, grooming & wellness in a tap",
  "Always free — no booking fees, ever",
];

const apiError = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || "Invalid details").join(" ");
  return detail?.msg || "Something went wrong. Please try again.";
};

export default function ClientJoin() {
  const { client, loading, login, register, startGoogle } = useClientAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "signin" ? "signin" : "signup");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submittedAuth = useRef(false);

  useEffect(() => {
    if (!loading && client && !submittedAuth.current) navigate("/marketplace", { replace: true });
  }, [client, loading, navigate]);

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "signup" && !termsAccepted) {
      setError("Please agree to the FYLLR Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    submittedAuth.current = true;
    try {
      if (mode === "signin") await login(form.email, form.password);
      else await register({ name: form.name, email: form.email, password: form.password, phone: form.phone || null, terms_accepted: true });
      navigate(mode === "signup" ? "/marketplace?welcome=1" : "/marketplace", { replace: true });
    } catch (err) {
      setError(apiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-espresso text-bone" data-testid="client-join-page">
      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* LEFT — pitch */}
        <div className="relative hidden lg:block">
          <img src={HERO_IMG} alt="Sunlit mineral pool at a luxury bathhouse" className="absolute inset-0 w-full h-full object-cover" data-testid="join-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/40 to-espresso/20" />
          <div className="relative z-10 h-full flex flex-col justify-end p-12 xl:p-16">
            <p className="text-[11px] tracking-[0.3em] uppercase text-mocha">FYLLR · Melbourne</p>
            <h2 className="mt-4 font-serif text-5xl xl:text-6xl leading-[0.95]">Good plans happen <em className="italic text-sand">last minute.</em></h2>
            <ul className="mt-8 space-y-3 max-w-sm">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-[15px] text-bone/80">
                  <Check size={16} className="text-mocha mt-0.5 shrink-0" strokeWidth={2} />{p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="flex items-center justify-center px-6 py-16 md:py-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <Link to="/" className="inline-block" data-testid="join-home-link">
              <img src="/brand/fyllr-wordmark-cream.png" alt="FYLLR" className="h-6 w-auto" />
            </Link>
            <h1 className="mt-10 font-serif text-4xl md:text-5xl leading-tight" data-testid="join-title">
              {mode === "signup" ? "Create your free account." : "Welcome back."}
            </h1>
            <p className="mt-3 text-[15px] text-bone/60" data-testid="join-sub">
              {mode === "signup"
                ? "Join FYLLR free and start booking Melbourne's best last-minute openings."
                : "Sign in to pick up where you left off."}
            </p>

            <button type="button" onClick={() => startGoogle({ termsAccepted })} disabled={mode === "signup" && !termsAccepted} className="mt-8 w-full flex items-center justify-center gap-3 border border-bone/25 rounded-full py-3 text-sm text-bone hover:bg-bone/10 disabled:cursor-not-allowed disabled:opacity-45 transition-colors" data-testid="join-google">
              <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              Continue with Google
            </button>

            <div className="my-5 flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-bone/40">
              <span className="h-px flex-1 bg-bone/15" /> or <span className="h-px flex-1 bg-bone/15" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && <Field icon={User} type="text" required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="join-name" />}
              <Field icon={Mail} type="email" required placeholder="Email address" value={form.email} onChange={set("email")} data-testid="join-email" />
              <Field icon={Lock} type="password" required minLength={8} placeholder={mode === "signup" ? "Create a password (8+ chars)" : "Password"} value={form.password} onChange={set("password")} data-testid="join-password" />
              {mode === "signup" && <Field icon={User} type="tel" placeholder="Mobile (optional)" value={form.phone} onChange={set("phone")} data-testid="join-phone" />}
              {mode === "signup" && <div className="flex items-start gap-3 px-1 pt-2 text-xs leading-relaxed text-bone/80" data-testid="join-terms-row"><Checkbox id="join-terms-checkbox" checked={termsAccepted} onCheckedChange={(checked) => setTermsAccepted(checked === true)} className="mt-0.5 h-5 w-5 border-bone/80 bg-transparent text-espresso shadow-none data-[state=checked]:border-bone data-[state=checked]:bg-bone focus-visible:ring-2 focus-visible:ring-sand" data-testid="join-terms-checkbox" /><label htmlFor="join-terms-checkbox" className="cursor-pointer">I agree to the FYLLR <Link to="/terms" className="text-bone underline underline-offset-2 hover:text-sand" data-testid="join-terms-link">Terms &amp; Conditions</Link> and <Link to="/privacy" className="text-bone underline underline-offset-2 hover:text-sand" data-testid="join-privacy-link">Privacy Policy</Link>.</label></div>}
              {error && <p className="text-sm text-sand" data-testid="join-error">{error}</p>}
              <button type="submit" disabled={busy || (mode === "signup" && !termsAccepted)} className="w-full flex items-center justify-center gap-2 rounded-full bg-bone text-ink py-3.5 text-sm hover:bg-sand disabled:opacity-60 transition-colors" data-testid="join-submit">
                {busy && <Loader2 size={15} className="animate-spin" />}
                {mode === "signup" ? "Create free account" : "Sign in"}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-bone/55">
              {mode === "signup" ? "Already have an account?" : "New to FYLLR?"}{" "}
              <button type="button" onClick={() => { setMode(mode === "signup" ? "signin" : "signup"); setTermsAccepted(false); setError(""); }} className="text-bone underline underline-offset-2 hover:text-sand" data-testid="join-toggle">
                {mode === "signup" ? "Sign in" : "Create a free account"}
              </button>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
